import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Formik, Form as FormikForm } from "formik";
import { Button, Nav, Tab } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  FaHeart,
  FaShoppingCart,
  FaStar,
  FaMinus,
  FaPlus,
  FaBoxOpen,
} from "react-icons/fa";
import { catalogApi, wishlistApi } from "../api/services";
import { getErrorMessage } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import FormField from "../components/forms/FormField";
import ProductCard from "./ProductCard";
import { resolveAssetUrl } from "../utils/assets";
import * as Yup from "yup";
import {
  isColorOptionName,
  optionValueColor,
  optionValueLabel,
  guessColorFromLabel,
  getDefaultCartExtras,
} from "../utils/variants";
import "./ProductDetails.css";
import Seo from "../components/Seo";
import { getProductSeo } from "../constants/seo";
import { breadcrumbSchema, productSchema } from "../utils/seoSchema";
import { ProductCardSkeleton, ProductDetailsSkeleton } from "../components/Skeleton";

const reviewSchema = Yup.object({
  rating: Yup.number().min(1).max(5).required("Rating is required"),
  comment: Yup.string().trim().min(3).required("Review is required"),
  name: Yup.string().trim().min(2).required("Name is required"),
  email: Yup.string().email().required("Email is required"),
});

function findMatchingVariant(variants, selected) {
  if (!variants?.length) return null;
  return (
    variants.find((v) =>
      Object.entries(selected).every(([k, val]) => v.attributes?.[k] === val)
    ) || null
  );
}

const ProductDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { addToCart, isInCart } = useCart();
  const { refreshWishlist } = useWishlist();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [activeImage, setActiveImage] = useState("");
  const [busy, setBusy] = useState(false);
  const [mainImgFailed, setMainImgFailed] = useState(false);
  const [failedThumbs, setFailedThumbs] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await catalogApi.getProduct(slug);
      const p = res.data.data.product;
      setProduct(p);
      setRelated(res.data.data.relatedProducts || []);
      setReviews(res.data.data.reviews || []);
      setActiveImage(resolveAssetUrl(p.images?.[0] || ""));
      setMainImgFailed(false);
      setFailedThumbs({});
      const defaults = {};
      (p.variantOptions || []).forEach((opt) => {
        const defVariant = p.variants?.find((v) => v.isDefault) || p.variants?.[0];
        const firstLabel = optionValueLabel(opt.values?.[0]);
        defaults[opt.name] =
          defVariant?.attributes?.[opt.name] || firstLabel || "";
      });
      setSelectedOptions(defaults);
    } catch (error) {
      toast.error(getErrorMessage(error, "Product not found"));
      navigate("/products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const matchedVariant = useMemo(
    () => findMatchingVariant(product?.variants, selectedOptions),
    [product, selectedOptions]
  );

  const variantInCart = useMemo(() => {
    if (!product?.id) return false;
    const variantId = matchedVariant?.id || null;
    if (product.variantOptions?.length) {
      return variantId ? isInCart(product.id, variantId) : false;
    }
    return isInCart(product.id);
  }, [product, matchedVariant, isInCart]);

  const displayPrice = matchedVariant?.price ?? product?.price ?? 0;
  const displaySku = matchedVariant?.sku || product?.sku || "";
  const variantImageRaw =
    (matchedVariant?.image && String(matchedVariant.image).trim()) || "";
  const variantImageResolved = variantImageRaw
    ? resolveAssetUrl(variantImageRaw)
    : "";

  // When the selected variant changes, show its image (if any).
  useEffect(() => {
    if (!variantImageResolved) return;
    setActiveImage(variantImageResolved);
    setMainImgFailed(false);
  }, [matchedVariant?.id, variantImageResolved]);

  const galleryImages = useMemo(() => {
    if (!product) return [];
    const list = [];
    const seen = new Set();
    const push = (url) => {
      if (!url) return;
      const key = String(url);
      if (seen.has(key)) return;
      seen.add(key);
      list.push(url);
    };
    (product.images || []).forEach(push);
    if (variantImageRaw) push(variantImageRaw);
    return list;
  }, [product, variantImageRaw]);

  const displayImage =
    activeImage ||
    resolveAssetUrl(product?.images?.[0] || product?.image || "");

  useEffect(() => {
    setMainImgFailed(false);
  }, [displayImage]);

  const requireAuth = (from) => {
    if (!isAuthenticated) {
      toast.info("Please login to continue");
      navigate("/login", { state: { from } });
      return false;
    }
    return true;
  };

  const handleAddToCart = async () => {
    setBusy(true);
    try {
      const extras = matchedVariant
        ? {
            variantId: matchedVariant.id,
            selectedAttributes: selectedOptions,
          }
        : getDefaultCartExtras(product);
      await addToCart(product.id, quantity, extras);
      toast.success("Added to cart");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not add to cart"));
    } finally {
      setBusy(false);
    }
  };

  const handleWishlist = async () => {
    if (!requireAuth(`/product/${slug}`)) return;
    setBusy(true);
    try {
      await wishlistApi.add(product.id);
      await refreshWishlist();
      toast.success("Added to wishlist");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not add to wishlist"));
    } finally {
      setBusy(false);
    }
  };

  if (loading || !product) {
    return (
      <main className="pd-page">
        <Seo title="Product | SG Office Supplies" path={`/product/${slug}`} />
        <div className="pd-container" style={{ padding: "24px 16px 48px" }}>
          <ProductDetailsSkeleton />
          <div style={{ marginTop: 40 }}>
            <ProductCardSkeleton count={4} />
          </div>
        </div>
      </main>
    );
  }

  const categoryName =
    typeof product.category === "object"
      ? product.category?.name
      : product.category;

  const categorySlug =
    typeof product.category === "object" ? product.category?.slug : "";

  const productSeo = getProductSeo(product);
  const productImage = product.images?.[0] || product.image || "";
  const resolvedImage = resolveAssetUrl(productImage);
  const schemaBreadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Shop", path: "/products" },
  ];
  if (categoryName && categorySlug) {
    schemaBreadcrumbs.push({
      name: categoryName,
      path: `/products?category=${categorySlug}`,
    });
  }
  schemaBreadcrumbs.push({
    name: product.name,
    path: `/product/${product.slug || slug}`,
  });

  return (
    <main className="pd-page">
      <Seo
        title={productSeo?.title}
        description={productSeo?.description}
        path={productSeo?.path}
        image={resolvedImage || productSeo?.image}
        jsonLd={[productSchema(product), breadcrumbSchema(schemaBreadcrumbs)]}
        jsonLdId="product-seo"
      />
      <section className="pd-breadcrumb">
        <Link to="/">Home</Link>
        <span>/</span>
        <Link to="/products">Shop</Link>
        <span>/</span>
        <span>{product.name}</span>
      </section>

      <section className="pd-main">
        <div className="pd-gallery">
          <div className="pd-main-image">
            {displayImage && !mainImgFailed ? (
              <img
                src={displayImage}
                alt={product.name}
                onError={() => setMainImgFailed(true)}
              />
            ) : (
              <div className="pd-image-fallback">
                <FaBoxOpen />
                <span>No image</span>
              </div>
            )}
          </div>
          {galleryImages.length > 1 && (
            <div className="pd-thumbs">
              {galleryImages.map((img) => {
                const src = resolveAssetUrl(img);
                if (failedThumbs[src]) return null;
                return (
                  <button
                    key={img}
                    type="button"
                    className={src === displayImage ? "active" : ""}
                    onClick={() => {
                      setActiveImage(src);
                      setMainImgFailed(false);
                    }}
                  >
                    <img
                      src={src}
                      alt=""
                      onError={() =>
                        setFailedThumbs((prev) => ({ ...prev, [src]: true }))
                      }
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="pd-info">
          <div className="pd-cats">
            {categoryName && <span>{categoryName}</span>}
            {(product.categories || []).map((c) => (
              <span key={c._id || c.slug || c}>{c.name || c}</span>
            ))}
          </div>
          <h1>{product.name}</h1>
          {(product.reviewCount || 0) > 0 && (
            <div className="pd-rating">
              <FaStar />
              <strong>{product.averageRating || 0}</strong>
              <span>out of 5 ({product.reviewCount} reviews)</span>
            </div>
          )}

          <div className="pd-price">
            <strong>AED {Number(displayPrice).toFixed(2)}</strong>
            {product.compareAtPrice ? (
              <s>AED {Number(product.compareAtPrice).toFixed(2)}</s>
            ) : null}
          </div>

          {product.specifications?.length > 0 && (
            <ul className="pd-specs">
              {product.specifications.map((s) => (
                <li key={`${s.label}-${s.value}`}>
                  <strong>{s.label} :</strong> {s.value}
                </li>
              ))}
            </ul>
          )}

          <div className="pd-meta">
            {displaySku && (
              <p>
                <strong>SKU:</strong> {displaySku}
              </p>
            )}
            {product.tags?.length > 0 && (
              <p>
                <strong>Tags:</strong> {product.tags.join(", ")}
              </p>
            )}
          </div>

          {(product.variantOptions || []).map((opt) => {
            const isColor =
              isColorOptionName(opt.name) ||
              (opt.values || []).some((v) => optionValueColor(v));
            return (
              <div
                className={`pd-option ${isColor ? "pd-option-color" : ""}`}
                key={opt.name}
              >
                <label>{opt.name}</label>
                <div className="pd-option-values">
                  {(opt.values || []).map((val) => {
                    const label = optionValueLabel(val);
                    const color =
                      optionValueColor(val) ||
                      (isColor ? guessColorFromLabel(label) : "");
                    const active = selectedOptions[opt.name] === label;
                    return (
                      <button
                        key={label}
                        type="button"
                        className={`${isColor ? "pd-color-chip" : ""} ${
                          active ? "active" : ""
                        }`}
                        onClick={() =>
                          setSelectedOptions((prev) => ({
                            ...prev,
                            [opt.name]: label,
                          }))
                        }
                      >
                        {isColor && (
                          <span
                            className="pd-color-swatch"
                            style={{ background: color || "#ccc" }}
                            aria-hidden
                          />
                        )}
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div className="pd-actions">
            <div className="pd-qty">
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              >
                <FaMinus />
              </button>
              <span>{quantity}</span>
              <button
                type="button"
                onClick={() => setQuantity((q) => q + 1)}
              >
                <FaPlus />
              </button>
            </div>

            {variantInCart ? (
              <Link to="/cart" className="pd-cart-btn">
                <FaShoppingCart /> Go to cart
              </Link>
            ) : (
              <button
                type="button"
                className="pd-cart-btn"
                disabled={busy}
                onClick={handleAddToCart}
              >
                <FaShoppingCart /> Add to cart
              </button>
            )}

            <button
              type="button"
              className="pd-wish-btn"
              disabled={busy}
              onClick={handleWishlist}
            >
              <FaHeart /> Wishlist
            </button>
          </div>
        </div>
      </section>

      <section className="pd-tabs-section">
        <Tab.Container defaultActiveKey="description">
          <Nav variant="tabs" className="pd-tabs">
            <Nav.Item>
              <Nav.Link eventKey="description">Description</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="reviews">
                Reviews ({reviews.length})
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <Tab.Content className="pd-tab-content">
            <Tab.Pane eventKey="description">
              <p style={{ whiteSpace: "pre-wrap" }}>
                {product.description ||
                  product.shortDescription ||
                  "No description available."}
              </p>
            </Tab.Pane>
            <Tab.Pane eventKey="reviews">
              {reviews.length === 0 ? (
                <p>There are no reviews yet.</p>
              ) : (
                <div className="pd-reviews">
                  {reviews.map((r) => (
                    <article key={r._id} className="pd-review">
                      <strong>
                        {r.name} · {r.rating}/5
                      </strong>
                      <p>{r.comment}</p>
                    </article>
                  ))}
                </div>
              )}

              <h3 className="mt-4">Write a review</h3>
              <Formik
                initialValues={{
                  rating: 5,
                  comment: "",
                  name: user?.name || "",
                  email: user?.email || "",
                }}
                enableReinitialize
                validationSchema={reviewSchema}
                onSubmit={async (values, { setSubmitting, resetForm }) => {
                  try {
                    await catalogApi.createReview(product.slug, values);
                    toast.success("Review submitted");
                    resetForm();
                    load();
                  } catch (error) {
                    toast.error(getErrorMessage(error, "Could not submit review"));
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {({ isSubmitting, values, handleChange }) => (
                  <FormikForm className="pd-review-form" noValidate>
                    <FormField name="name" label="Name" />
                    <FormField name="email" type="email" label="Email" />
                    <div className="mb-3">
                      <label className="form-label">Your rating</label>
                      <select
                        className="form-select"
                        name="rating"
                        value={values.rating}
                        onChange={handleChange}
                      >
                        <option value={5}>Perfect</option>
                        <option value={4}>Good</option>
                        <option value={3}>Average</option>
                        <option value={2}>Not that bad</option>
                        <option value={1}>Very poor</option>
                      </select>
                    </div>
                    <FormField
                      name="comment"
                      type="textarea"
                      rows={4}
                      label="Your review"
                    />
                    <Button type="submit" disabled={isSubmitting} className="pd-cart-btn">
                      {isSubmitting ? "Submitting..." : "Submit review"}
                    </Button>
                  </FormikForm>
                )}
              </Formik>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </section>

      {related.length > 0 && (
        <section className="pd-related">
          <h2>Related Products</h2>
          <div className="products-grid">
            {related.map((p) => (
              <ProductCard
                key={p.id}
                product={p}
                onAddToCart={async (item) => {
                  try {
                    const extras = getDefaultCartExtras(item);
                    await addToCart(item.id, 1, extras);
                    toast.success("Added to cart");
                  } catch (error) {
                    toast.error(getErrorMessage(error, "Could not add to cart"));
                  }
                }}
              />
            ))}
          </div>
        </section>
      )}
    </main>
  );
};

export default ProductDetails;
